import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Filter } from 'lucide-react';
import { useApiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const accent = '#6366f1';
const accentLight = '#f1f5ff';

const initialForm = {
  registrationNumber: '',
  chassisNumber: '',
  engineNumber: '',
  name: '',
  vehicleModel: '',
  vehicleMake: '',
  registeredIn: '',
  mileage: '',
  registeredInCity: '',
  color: '',
  condition: 'used',
  cplcStatus: 'pending',
  description: ''
};

const Cars = () => {
  const { createCar, getAllCars, updateCar, deleteCar, getCarStats } = useApiService();
  const { user: currentUser } = useAuth();

  const [cars, setCars] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [itemsPerPage] = useState(10);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingCar, setViewingCar] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    loadCars();
    loadStats();
  }, []);

  const loadCars = async () => {
    try {
      setLoading(true);
      const response = await getAllCars();
      setCars(response.cars || response);
      setError(null);
    } catch (err) {
      setError('Failed to load cars. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const s = await getCarStats();
      setStats(s);
    } catch {}
  };

  const filteredCars = cars.filter(car =>
    (car.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (car.vehicleModel || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (car.vehicleMake || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (car.registrationNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (car.chassisNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (car.engineNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (car.color || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCars = filteredCars.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCars.length / itemsPerPage);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddCar = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Car name is required!');
      return;
    }
    
  
    // Check if either registration number OR (chassis AND engine) is provided
    const hasRegistration = formData.registrationNumber && formData.registrationNumber.trim();
    const hasChassisAndEngine = formData.chassisNumber && formData.chassisNumber.trim() && formData.engineNumber && formData.engineNumber.trim();
    
    if (!hasRegistration && !hasChassisAndEngine) {
      alert('Either registration number OR (chassis number AND engine number) is required!');
      return;
    }
    try {
      setSubmitting(true);
      const newCar = await createCar(formData);
      if (newCar) {
        await loadCars();
        setFormData(initialForm);
        setShowAddModal(false);
      }
    } catch (err) {
      alert('Failed to create car. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (car) => {
    setEditingCar(car);
    setFormData({
      registrationNumber: car.registrationNumber || '',
      chassisNumber: car.chassisNumber || '',
      engineNumber: car.engineNumber || '',
      name: car.name || '',
      vehicleModel: car.vehicleModel || '',
      vehicleMake: car.vehicleMake || '',
      registeredIn: car.registeredIn || '',
      mileage: car.mileage || '',
      registeredInCity: car.registeredInCity || '',
      color: car.color || '',
      condition: car.condition || 'used',
      cplcStatus: car.cplcStatus || 'pending',
      description: car.description || ''
    });
    setShowEditModal(true);
  };

  const handleEditCar = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      alert('Car name is required!');
      return;
    }
    
    // Check if either registration number OR (chassis AND engine) is provided
    const hasRegistration = formData.registrationNumber && formData.registrationNumber.trim();
    const hasChassisAndEngine = formData.chassisNumber && formData.chassisNumber.trim() && formData.engineNumber && formData.engineNumber.trim();
    
    if (!hasRegistration && !hasChassisAndEngine) {
      alert('Either registration number OR (chassis number AND engine number) is required!');
      return;
    }
    
    try {
      setSubmitting(true);
      await updateCar(editingCar.id, formData);
      await loadCars();
      setFormData(initialForm);
      setShowEditModal(false);
      setEditingCar(null);
    } catch (err) {
      alert('Failed to update car. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCar = async (carId) => {
    if (window.confirm('Are you sure you want to delete this car?')) {
      try {
        await deleteCar(carId);
        await loadCars();
      } catch (err) {
        const message = err?.details?.message || err?.message || 'Failed to delete car. Please try again.';
        alert(message);
      }
    }
  };

  const openViewModal = (car) => {
    setViewingCar(car);
    setShowViewModal(true);
  };

  const handleCloseModal = () => {
    setFormData(initialForm);
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingCar(null);
    setViewingCar(null);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '4px solid #e5e7eb', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }}></div>
          <p style={{ color: '#6b7280', margin: 0 }}>Loading cars...</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#dc2626', margin: '0 0 1rem' }}>{error}</p>
          <button onClick={loadCars} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.5rem 1rem', cursor: 'pointer' }}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', background: '#fafbfc', minHeight: '100vh', padding: '2rem 1.5rem', width: '100%', boxSizing: 'border-box' }}>
      {/* Header Section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '.5rem' }}>
        <div>
          <h1 className="fw-bold mb-2" style={{ color: '#1f2937', fontSize: '1.75rem', letterSpacing: '-0.025em' }}>Car Stock Management</h1>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>Manage and monitor your showroom car inventory</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold" style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem' }}>
            <Filter size={16} />
            <span className="d-none d-sm-inline">Filter</span>
          </button>
          <button className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold" style={{ background: accent, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.875rem', boxShadow: '0 1px 3px 0 rgba(99, 102, 241, 0.1), 0 1px 2px 0 rgba(99, 102, 241, 0.06)' }} onClick={() => setShowAddModal(true)}>
            <Plus size={16} />
            <span className="d-none d-sm-inline">Add Car</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '.5rem' }}>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: accentLight, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: accent, fontSize: '1rem', fontWeight: '600' }}>{stats.totalCars || cars.length}</span>
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Total Cars</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>Available inventory</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#f0fdf4', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#16a34a', fontSize: '1rem', fontWeight: '600' }}>{stats.newCars || cars.filter(c => c.condition === 'new').length}</span>
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>New Cars</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>Brand new vehicles</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#fef2f2', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#dc2626', fontSize: '1rem', fontWeight: '600' }}>{stats.usedCars || cars.filter(c => c.condition === 'used').length}</span>
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Used Cars</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>Pre-owned vehicles</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="card border-0 shadow-sm h-100" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center">
                <div style={{ width: 40, height: 40, background: '#f0f9ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ color: '#0284c7', fontSize: '1rem', fontWeight: '600' }}>{filteredCars.length}</span>
                </div>
                <div style={{ marginLeft: 12 }}>
                  <h6 className="mb-1 fw-semibold" style={{ color: '#1f2937', fontSize: '0.875rem' }}>Filtered Results</h6>
                  <p className="mb-0 text-muted" style={{ fontSize: '0.75rem' }}>Search results</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card border-0 shadow-sm mb-4" style={{ background: '#fff', borderRadius: '12px', width: '100%' }}>
        <div className="card-body p-3 p-md-4">
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search cars by name, make, model, registration, chassis, engine, or color..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', paddingLeft: '16px', fontSize: '0.875rem' }}
                />
              </div>
            </div>
            <div style={{ minWidth: 180, textAlign: 'right', flex: 'none' }}>
              <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                Showing {filteredCars.length} of {cars.length} cars
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Cars Table */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Car</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Registration</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }} className="d-none d-md-table-cell">Chassis / Engine</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }} className="d-none d-md-table-cell">Condition</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }} className="d-none d-lg-table-cell">Deal Status</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }} className="d-none d-lg-table-cell">Color</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }} className="d-none d-lg-table-cell">Mileage</th>
              <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }} className="d-none d-lg-table-cell">Updated By</th>
              <th style={{ padding: '1rem', textAlign: 'right', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentCars.map((car) => (
              <tr key={car.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                  <div style={{ fontWeight: 600 }}>{car.name}</div>
                  <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>{car.vehicleMake} {car.vehicleModel}</div>
                </td>
                <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }}>
                  {car.registrationNumber || 'N/A'}
                </td>
                <td style={{ padding: '1rem', fontSize: '0.8rem', color: '#4b5563' }} className="d-none d-md-table-cell">
                  <div>Chassis: {car.chassisNumber || 'N/A'}</div>
                  <div>Engine: {car.engineNumber || 'N/A'}</div>
                </td>
                <td style={{ padding: '1rem' }} className="d-none d-md-table-cell">
                  <span style={{ 
                    padding: '0.35rem 0.75rem', 
                    borderRadius: '999px', 
                    fontSize: '0.75rem', 
                    fontWeight: '700',
                    background: car.condition === 'new' ? '#d1fae5' : '#fef2f2',
                    color: car.condition === 'new' ? '#065f46' : '#b91c1c',
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em'
                  }}>
                    {car.condition || 'N/A'}
                  </span>
                </td>
                <td style={{ padding: '1rem' }} className="d-none d-lg-table-cell">
                  <span style={{
                    padding: '0.35rem 0.75rem',
                    borderRadius: '999px',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    background: car.dealStatus === 'Sold' ? '#d1fae5'
                      : car.dealStatus === 'Purchased' ? '#f0f9ff'
                      : '#f3f4f6',
                    color: car.dealStatus === 'Sold' ? '#065f46'
                      : car.dealStatus === 'Purchased' ? '#0ea5e9'
                      : '#374151',
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em'
                  }}>
                    {car.dealStatus || 'None'}
                  </span>
                </td>
                <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }} className="d-none d-lg-table-cell">
                  {car.color || 'N/A'}
                </td>
                <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }} className="d-none d-lg-table-cell">
                  {car.mileage ? `${car.mileage} km` : 'N/A'}
                </td>
                <td style={{ padding: '1rem', color: '#1f2937', fontSize: '0.875rem' }} className="d-none d-lg-table-cell">
                  {car.updatedByName || car.updatedBy || 'Not set'}
                </td>
                <td style={{ padding: '1rem' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <button 
                      onClick={() => openViewModal(car)}
                      style={{ background: 'none', border: 'none', padding: '0.5rem', borderRadius: '6px', cursor: 'pointer', color: accent }}
                      title="View"
                    >
                      <Eye size={16} />
                    </button>
                    <button 
                      onClick={() => openEditModal(car)}
                      style={{ background: 'none', border: 'none', padding: '0.5rem', borderRadius: '6px', cursor: 'pointer', color: '#0284c7' }}
                      title="Edit"
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      onClick={() => handleDeleteCar(car.id)}
                      style={{ background: 'none', border: 'none', padding: '0.5rem', borderRadius: '6px', cursor: 'pointer', color: '#dc2626' }}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
          {[...Array(totalPages)].map((_, i) => (
            <button key={i} onClick={() => setCurrentPage(i + 1)} style={{ background: currentPage === i + 1 ? accent : '#f3f4f6', color: currentPage === i + 1 ? '#fff' : '#1f2937', border: 'none', borderRadius: '6px', margin: '0 4px', padding: '0.4rem 0.9rem', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer' }}>{i + 1}</button>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '16px', padding: '2rem', minWidth: 400, maxWidth: 500, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>{showAddModal ? 'Add New Car' : 'Edit Car'}</h3>
              <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280', padding: '0.25rem' }}>&times;</button>
            </div>
            <form onSubmit={showAddModal ? handleAddCar : handleEditCar}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Car Name*</label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} className="form-control" required style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s' }} placeholder="Enter car name" />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Vehicle Make*</label>
                  <input type="text" name="vehicleMake" value={formData.vehicleMake} onChange={handleInputChange} className="form-control"  style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s' }} placeholder="Enter vehicle make (e.g., Toyota, Honda)" />
                  {/* <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>Required by system</small> */}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Vehicle Model*</label>
                  <input type="text" name="vehicleModel" value={formData.vehicleModel} onChange={handleInputChange} className="form-control"  style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s' }} placeholder="Enter vehicle model (e.g., Corolla, Civic)" />
                  {/* <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>Required by system</small> */}
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Color</label>
                  <input type="text" name="color" value={formData.color} onChange={handleInputChange} className="form-control" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s' }} placeholder="Enter color" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Registration Number</label>
                  <input type="text" name="registrationNumber" value={formData.registrationNumber} onChange={handleInputChange} className="form-control" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s' }} placeholder="Enter registration number" />
                  <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>Required if chassis & engine not provided</small>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Chassis Number</label>
                  <input type="text" name="chassisNumber" value={formData.chassisNumber} onChange={handleInputChange} className="form-control" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s' }} placeholder="Enter chassis number" />
                  <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>Required with engine number if no registration</small>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Engine Number</label>
                  <input type="text" name="engineNumber" value={formData.engineNumber} onChange={handleInputChange} className="form-control" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s' }} placeholder="Enter engine number" />
                  <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>Required with chassis number if no registration</small>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Registered In</label>
                  <input type="text" name="registeredIn" value={formData.registeredIn} onChange={handleInputChange} className="form-control" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s' }} placeholder="Enter registration year" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Mileage (km)</label>
                  <input type="number" name="mileage" value={formData.mileage} onChange={handleInputChange} className="form-control" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s' }} placeholder="Enter mileage" min="0" />
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Registered In City</label>
                  <input type="text" name="registeredInCity" value={formData.registeredInCity} onChange={handleInputChange} className="form-control" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s' }} placeholder="Karachi, Lahore, or custom" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Condition</label>
                  <select name="condition" value={formData.condition} onChange={handleInputChange} className="form-control" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s' }}>
                    <option value="new">New</option>
                    <option value="used">Used</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>CPLC Status</label>
                  <select name="cplcStatus" value={formData.cplcStatus || 'pending'} onChange={handleInputChange} className="form-control" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s' }}>
                    <option value="clear">Clear</option>
                    <option value="blocked">Blocked</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Description</label>
                <textarea name="description" value={formData.description} onChange={handleInputChange} className="form-control" style={{ width: '100%', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s', resize: 'vertical' }} rows={3} placeholder="Enter car description" />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                <button type="button" className="btn" style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem 1.5rem', fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.2s' }} onClick={handleCloseModal}>Cancel</button>
                <button type="submit" className="btn" style={{ background: accent, color: '#fff', border: 'none', borderRadius: '8px', padding: '0.75rem 1.5rem', fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 1px 3px 0 rgba(99, 102, 241, 0.1), 0 1px 2px 0 rgba(99, 102, 241, 0.06)' }} disabled={submitting}>{submitting ? 'Saving...' : (showAddModal ? 'Add Car' : 'Update Car')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewingCar && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '16px', padding: '2rem', minWidth: 400, maxWidth: 500, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '600' }}>Car Details</h3>
              <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280', padding: '0.25rem' }}>&times;</button>
            </div>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Car Name</label>
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCar.name || 'Not provided'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Vehicle Make</label>
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCar.vehicleMake || 'Not provided'}</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Vehicle Model</label>
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCar.vehicleModel || 'Not provided'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Color</label>
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCar.color || 'Not provided'}</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Registration Number</label>
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCar.registrationNumber || 'Not provided'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>CPLC Status</label>
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCar.cplcStatus || 'Not provided'}</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Deal Status</label>
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCar.dealStatus || 'None'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Updated By</label>
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCar.updatedByName || viewingCar.updatedBy || 'Not set'}</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Chassis Number</label>
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCar.chassisNumber || 'Not provided'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Engine Number</label>
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCar.engineNumber || 'Not provided'}</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Registered In</label>
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCar.registeredIn || 'Not provided'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Mileage</label>
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCar.mileage ? `${viewingCar.mileage} km` : 'Not provided'}</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Registered In City</label>
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCar.registeredInCity || 'Not provided'}</div>
                </div>
                <div>
                  <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Condition</label>
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem' }}>{viewingCar.condition || 'Not provided'}</div>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: '600', color: '#374151', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Description</label>
                <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '0.875rem', minHeight: '3rem' }}>{viewingCar.description || 'Not provided'}</div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem', marginTop: '1.5rem' }}>
              <button type="button" className="btn" style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0.75rem 1.5rem', fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.2s' }} onClick={handleCloseModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cars; 